import React, { useContext, useState, useEffect, useCallback } from 'react'
import styled, { css } from 'styled-components'
import { FaPlus } from 'react-icons/fa'
import { up } from 'styled-breakpoints'

import courseTheme from '../../constants/theme'
import { Context } from './UnitsTableProvider'
import LessonList from './LessonList'

const UnitList = styled.section`
  ${({ loadMore }) => css`
    position: relative;

    ${!loadMore &&
    css`
      max-height: 500px;
      overflow: hidden;

      &:after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 100px;
        background-image: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 0.8),
          rgba(255, 255, 255, 1)
        );
      }
    `}
  `}
`

const Unit = styled.section`
  display: grid;
  grid-template-areas:
    'level title'
    'level desc'
    'lesson-list lesson-list';
  column-gap: ${courseTheme.gutter};
  margin-bottom: ${courseTheme.gutter};

  ${up('medium')} {
    grid-template-areas:
      'level title'
      'level desc'
      '_ lesson-list';
    grid-template-columns: 100px 1fr;
  }
`

const LevelWrapper = styled.div`
  ${({ theme }) => css`
    grid-area: level;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-right: 1px solid ${theme.colors.neutral.gray700};
    font-family: ${theme.fontFamily.heading};
    padding-right: ${courseTheme.gutter};

    ${up('medium')} {
      padding-right: 0;
    }
  `}
`

const LevelTitle = styled.div`
  ${({ theme }) => css`
    color: ${theme.colors.neutral.gray900};
  `}
`

const LevelNo = styled.div`
  ${({ theme }) => css`
    color: ${theme.colors.neutral.gray1200};
  `}
`

const Title = styled.div`
  ${({ theme }) => css`
    grid-area: title;
    font-family: ${theme.fontFamily.heading};
  `}
`

const Desc = styled.div`
  ${({ theme }) => css`
    grid-area: desc;
    color: ${theme.colors.neutral.gray900};
  `}
`

const Day = styled.div`
  ${({ theme }) => css`
    position: relative;
    text-align: center;
    color: ${theme.colors.neutral.gray900};
    font-size: ${theme.fontSizes.medium};
    background-color: ${theme.colors.neutral.gray200};
    margin-bottom: ${theme.spacers.normal};

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 20px;
      height: 3px;
      top: 50%;
      transform: translateY(-50%);
      background-color: ${theme.colors.main.secondary};
      border-radius: 2rem;
    }

    &::before {
      transform: translateX(-30px);
    }

    &::after {
      transform: translateX(10px);
    }
  `}
`

const StyledLessonList = styled(LessonList)`
  grid-area: lesson-list;
`

const LoadMoreButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    padding: ${theme.spacers.xsmall} ${theme.spacers.small};
    margin: 0 auto;
    margin-bottom: ${courseTheme.gutter};
    font-size: ${theme.fontSizes.small};
    background-color: #37bc9b;
    color: #fff;
    border-radius: 2rem;
    border: none;
    outline: none;
    cursor: pointer;
  `}
`

const LoadMoreButtonIcon = styled(FaPlus)`
  margin-right: ${({ theme }) => theme.spacers.xsmall};
`

function Level({ level }) {
  return (
    <LevelWrapper>
      <LevelTitle>Unit</LevelTitle>
      <LevelNo>{level}</LevelNo>
    </LevelWrapper>
  )
}

function OnlineUnits({ courseType, courseSlug, units }) {
  return units.map(({ title, desc, lessons }, index) => (
    <Unit key={title}>
      <Level level={index + 1}></Level>
      <Title>{title}</Title>
      <Desc>{desc}</Desc>
      <StyledLessonList
        courseType={courseType}
        courseSlug={courseSlug}
        lessons={lessons}
      ></StyledLessonList>
    </Unit>
  ))
}

function EnterpriseUnits({ courseType, courseSlug, units: allUnits }) {
  return allUnits.map(({ day, units }, dayIndex) => {
    const prevUnitsLength = allUnits
      .slice(0, dayIndex)
      .reduce((acc, day) => day.units.length + acc, 0)

    return (
      <div key={day}>
        <Day>วันที่: {day}</Day>
        {units.map(({ title, desc, lessons }, index) => (
          <Unit key={title}>
            <Level level={prevUnitsLength + index + 1}></Level>
            <Title>{title}</Title>
            <Desc>{desc}</Desc>
            <StyledLessonList
              courseType={courseType}
              courseSlug={courseSlug}
              lessons={lessons}
            ></StyledLessonList>
          </Unit>
        ))}
      </div>
    )
  })
}

function UnitsTable() {
  const { units, courseType, courseSlug } = useContext(Context)
  const [loadMore, setLoadMore] = useState(false)

  const showLoadMoreButton = useCallback(() => setLoadMore(true), [setLoadMore])

  useEffect(() => {
    setLoadMore(units.length <= 2)
  }, [courseType, units, setLoadMore])

  return (
    <>
      <UnitList loadMore={loadMore}>
        {courseType === 'online' ? (
          <OnlineUnits
            courseType={courseType}
            courseSlug={courseSlug}
            units={units}
          ></OnlineUnits>
        ) : (
          <EnterpriseUnits
            courseType={courseType}
            courseSlug={courseSlug}
            units={units}
          ></EnterpriseUnits>
        )}
      </UnitList>
      {!loadMore && (
        <LoadMoreButton onClick={showLoadMoreButton}>
          <LoadMoreButtonIcon></LoadMoreButtonIcon>
          ดูทั้งหมด
        </LoadMoreButton>
      )}
    </>
  )
}

export default UnitsTable
